import { ClubEmptyState } from "@/features/MyClub/EmptyClub/ClubEmptyState";
import { ClubEmptyStateContext } from "@/features/MyClub/EmptyClub/emptyClubCopy";
import { clubIsEmptySelector } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { ReactNode } from "react";
import { useSelector } from "react-redux";

type Props = {
  /** Set when the search term itself was rejected (too short, bad characters). */
  searchTermErrorMessage?: string;
  /** The active My Club / My Evolutions / Active Squad quick filter, if any. */
  category?: string;
  /** Which club empty state to show. Picks its copy and tracking. */
  clubContext: ClubEmptyStateContext;
  /** The page this list belongs to ("Players", "Evolutions"), for signup tracking. */
  section: string;
  /** The page's own My Evolutions empty state, with its tracking props. */
  evolutionsEmptyState: ReactNode;
};

/**
 * Picks the right "nothing to show" message for a players list: a bad search
 * term, an evolution collection that needs an import, a club that needs one, or
 * plain filters that matched nothing.
 *
 * The club branch is gated on the club really being empty, so a filter that
 * matched nothing still reads "No players found" rather than asking for an
 * import the user doesn't need.
 */
export const PlayersListEmptyState = ({
  searchTermErrorMessage,
  category,
  clubContext,
  section,
  evolutionsEmptyState,
}: Props) => {
  const { t } = useTranslation("common");
  const clubIsEmpty = useSelector(clubIsEmptySelector);

  if (searchTermErrorMessage) {
    return (
      <div className="text-center text-white text-sm">
        {searchTermErrorMessage}
      </div>
    );
  }
  if (category === "Evolved") return <>{evolutionsEmptyState}</>;
  // Not gated on an empty club: a full club can still have no active squad.
  if (category === "ActiveSquad") {
    return <ClubEmptyState context="active-squad" section={section} />;
  }
  if (category === "Owned" && clubIsEmpty) {
    return <ClubEmptyState context={clubContext} section={section} />;
  }
  return (
    <div className="text-center text-white text-sm">{t("noPlayersFound")}</div>
  );
};
